import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  Divider,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { styled } from '@mui/material/styles';
import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '../../../urls';
import { COLORS } from '@/theme/colors';
import { formatCurrencyNoUnit } from '@/lib/formatters';
import { DisputeDialog } from '@/components/dispute-dialog/dispute-dialog';
import DisputesModal from '@/components/disputes-table/disputes-modal';
import { getUser } from '@/lib/common';
import { ActionCenterItemV2 } from '@cloudcost/shared-types/action-center-v2';
import ResourceDetailsModal from '@/components/resource-details/resource-details-modal';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  zIndex: 1300, // Just above default modal z-index (1300)
  '& .MuiDialog-paper': {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    maxWidth: '1200px',
    width: '100%',
    margin: '32px auto',
    padding: 0,
    overflow: 'hidden',
    zIndex: 1301, // Above the backdrop
    position: 'relative',
  },
  '& .MuiBackdrop-root': {
    zIndex: 1299,
  },
}));

const ModalPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  borderRadius: '14px',
  boxShadow: '0 24px 60px rgba(16,35,58,.22)',
  height: 'auto',
  maxHeight: 'calc(100vh - 64px)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  position: 'relative',
  zIndex: 1301, // Ensure it's above the backdrop
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  borderBottom: `1px solid #EEF1F6`,
  padding: '16px 24px',
  '& .MuiTypography-root': {
    fontSize: '1.25rem',
    fontWeight: 900,
    color: '#16233A',
  },
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  overflow: 'auto',
  flex: 1,
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: COLORS.dialogTrackBackground,
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: COLORS.mutedBlue,
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: COLORS.mutedBlueHover,
  },
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: '16px 24px',
  backgroundColor: '#FFFFFF',
  borderTop: `1px solid #EEF1F6`,
}));

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#1C71E3',
  color: 'white',
  '&:hover': {
    backgroundColor: '#1558B0',
  },
}));

const CancelButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  color: '#1C71E3',
  border: `1px solid #1C71E3`,
  '&:hover': {
    backgroundColor: COLORS.buttonHover,
  },
}));

const RejectButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  color: '#E23D3D',
  border: `1px solid #F1C4C4`,
  '&:hover': {
    backgroundColor: '#FDEBEB',
  },
}));

const ScrollableContent = styled(Box)(({ theme }) => ({
  height: '160px',
  border: `1px solid #E7ECF3`,
  borderRadius: '9px',
  padding: '16px',
  overflow: 'auto',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: COLORS.dialogTrackBackground,
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: COLORS.mutedBlue,
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: COLORS.mutedBlueHover,
  },
}));

interface RecommendationModalProps {
  recommendation: any;
  onClose: () => void;
  onActionComplete?: () => void;
}

const RecommendationModal = ({
  recommendation,
  onClose,
  onActionComplete,
}: RecommendationModalProps) => {
  const [detailedRecommendation, setDetailedRecommendation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [disputeDialogOpen, setDisputeDialogOpen] = useState(false);
  const [actionCenterItem, setActionCenterItem] = useState<ActionCenterItemV2 | null>(null);
  const [responsibleUserId, setResponsibleUserId] = useState<number | null>(null);
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [disputeModalOpen, setDisputeModalOpen] = useState(false);
  const [dispute, setDispute] = useState<any[]>([]);
  const [resourceModalOpen, setResourceModalOpen] = useState(false);
  const [selectedResourceId, setSelectedResourceId] = useState<number | null>(null);

  const user = getUser();

  useEffect(() => {
    // Reset detailed recommendation when recommendation changes
    setDetailedRecommendation(null);
    
    if (recommendation?.id) {
      setLoading(true);
      fetchWrapper
        .get(`${urls.DevOps.getRecommendationDetails}/${recommendation.id}`)
        .then((data) => {
          if (data) {
            setDetailedRecommendation(data);
          } else {
            // If no detailed data, use the recommendation object itself
            setDetailedRecommendation(recommendation);
          }
          setLoading(false);
        })
        .catch((error) => {
          console.error('Error fetching recommendation details:', error);
          // Fallback to using the recommendation object itself if fetch fails
          setDetailedRecommendation(recommendation);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [recommendation]);

  if (!recommendation) return null;

  const onMarkAsDeclined = () => {
    fetchWrapper
      .post(
        urls.DevOps.rejectRecommendation.replace(':id', recommendation.id),
        {},
      )
      .then((data) => {
        setLoading(false);
        onActionComplete?.();
        onClose();
      })
      .catch((error) => {
        console.error('Error setting recommendation as declined:', error);
        setLoading(false);
        onClose();
      });
  };

  const onMarkAsImplemented = () => {
    fetchWrapper
      .post(
        urls.DevOps.implementRecommendation.replace(':id', recommendation.id),
        {},
      )
      .then((data) => {
        setLoading(false);
        onActionComplete?.();
        onClose();
      })
      .catch((error) => {
        console.error('Error setting recommendation as implemented:', error);
        setLoading(false);
        onClose();
      });
  };

  /**
   * Opens dispute modal if dispute exists, otherwise opens dispute creation dialog
   */
  const handleOpenDisputeDialog = async () => {
    try {
      setLoading(true);
      
      // Get recommendation details to determine responsible user
      const recommendationDetails = detailedRecommendation ?? recommendation;
      
      // Fetch optimization action center items
      const actionCenterItems = await fetchWrapper.get(
        urls.DevOps.getOptimizationActionCenterV2,
      );
      
      // Find the action center item for this recommendation
      // For resource-level: match by id_recommendation
      // For subscription-level: match by cloud_account_id and scope
      let item = actionCenterItems.find(
        (item: ActionCenterItemV2) =>
          item.id_recommendation === recommendation.id,
      );
      
      // If not found and it's a subscription-level recommendation (no id_resource),
      // try to find CloudAccount scope item
      if (!item && !recommendationDetails.id_resource && recommendationDetails.cloud_account_id) {
        item = actionCenterItems.find(
          (item: ActionCenterItemV2) =>
            item.scope === 'CloudAccount' &&
            item.cloud_account_id === recommendationDetails.cloud_account_id,
        );
      }
      
      // Check if dispute already exists
      if (item?.id_dispute) {
        // Dispute exists - fetch and open it
        try {
          const disputesList = await fetchWrapper.get(urls.DevOps.disputesList);
          const existingDispute = disputesList.filter(
            (row: any) => row.id === item.id_dispute,
          );
          
        if (existingDispute.length > 0) {
          // Store the action center item for use in DisputesModal
          setActionCenterItem(item);
          setDispute(existingDispute);
          setDisputeModalOpen(true);
          setLoading(false);
          return;
        }
        } catch (error) {
          console.error('Error fetching existing dispute:', error);
          // Continue to create new dispute if fetch fails
        }
      }
      
      // Fetch available users first (needed for lookup)
      const users = await fetchWrapper.get(urls.DevOps.getUsersAll);
      setAvailableUsers(users);
      
      // No dispute exists - open creation dialog
      // Determine responsible user ID
      if (item) {
        // Use responsible_id from action center item
        setActionCenterItem(item);
        setResponsibleUserId(item.responsible_id);
      } else {
        // If no action center item found, try to get responsible user from recommendation details
        // For subscription-level recommendations, use cloud account owner
        const responsibleUsername = recommendationDetails.responsible_user;
        if (responsibleUsername && users && users.length > 0) {
          const responsibleUser = users.find(
            (u: any) => u.username === responsibleUsername
          );
          if (responsibleUser) {
            setResponsibleUserId(responsibleUser.id);
          } else {
            console.warn('Responsible user not found in users list');
            setResponsibleUserId(null);
          }
        } else {
          console.warn('Action center item not found for recommendation, will create dispute without action_center_id');
          setResponsibleUserId(null);
        }
        setActionCenterItem(null);
      }
      
      setDisputeDialogOpen(true);
      setLoading(false);
    } catch (error) {
      console.error('Error opening dispute dialog:', error);
      setLoading(false);
    }
  };

  /**
   * Handles dispute submission - creates dispute and opens modal
   */
  const handleDisputeSubmit = async (title: string, receiverId: number) => {
    try {
      setLoading(true);
      
      // Create dispute with action center item attached (if available)
      await fetchWrapper.post(urls.DevOps.createDisputeV2, {
        id_receiver: receiverId,
        sender: user?.username,
        title: title,
        type: 'Recommendation',
        action_center_id: actionCenterItem?.id || null,
      });

      // Get the created dispute ID and fetch dispute data
      const maxDisputeData = await fetchWrapper.get(
        urls.DevOps.getMaxDispute,
      );
      const disputeId = maxDisputeData[0]?.disputeid;

      if (disputeId) {
        // Fetch dispute list and filter by ID (same pattern as action center)
        const disputesList = await fetchWrapper.get(urls.DevOps.disputesList);
        const createdDispute = disputesList.filter(
          (row: any) => row.id === disputeId,
        );
        
        if (createdDispute.length > 0) {
          setDispute(createdDispute);
          setDisputeDialogOpen(false);
          setDisputeModalOpen(true);
        }
      }
    } catch (error) {
      console.error('Error creating dispute:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handles clicking on resource name to open resource details modal
   */
  const handleOpenResourceDetails = () => {
    const data = detailedRecommendation ?? recommendation;
    if (data?.id_resource) {
      setSelectedResourceId(data.id_resource);
      setResourceModalOpen(true);
    }
  };

  const displayData = detailedRecommendation ?? recommendation;
  const isResourceRecommendation = !!displayData?.id_resource;
  const currency = displayData?.currency || 'USD';

  return (
    <>
    <StyledDialog open={!!recommendation} onClose={onClose} maxWidth="lg">
      <ModalPaper>
        <StyledDialogTitle>
          <Typography variant="h6">{displayData.name}</Typography>
        </StyledDialogTitle>

        <StyledDialogContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={2}
            mt={2}>
            <Box>
              <Typography variant="subtitle1" color="textSecondary">
                Optimization Recommendation
              </Typography>
            </Box>
            <Box textAlign="right">
              <Typography variant="body2" fontWeight="bold">
                Monthly savings potential:
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: '#16A55C', fontWeight: 'bold' }}>
                {displayData.monthly_savings ? formatCurrencyNoUnit(displayData.monthly_savings) : 'n/a'} {displayData.currency}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Monthly forecast (current month):
              </Typography>
              <Typography variant="body1">
                {displayData.current_month_forecast
                  ? formatCurrencyNoUnit(displayData.current_month_forecast)
                  : 'n/a'}{' '}
                {displayData.currency}
              </Typography>
            </Box>
          </Box>

          <Box mb={2}>
            <Typography variant="body2" color="textSecondary">
              {displayData.description || 'This recommendation has been identified to help optimize your cloud costs. Implementing this recommendation could lead to significant savings.'}
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid #E7ECF3',
                  boxShadow: '0 1px 2px rgba(16,35,58,.04)',
                }}>
                <CardContent sx={{ flex: 1 }}>
                  <Typography fontWeight="bold" color="#16233A">
                    Resource Details
                  </Typography>
                  <Typography variant="body2">
                    <strong>Department:</strong>{' '}
                    {displayData.department_name ?? 'not assigned'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>System:</strong>{' '}
                    {displayData.system_name ?? 'not assigned'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Responsible:</strong> {displayData.responsible_user}
                  </Typography>

                  <Divider sx={{ my: 1.5 }} />

                  {displayData.cloud_account_name && (
                    <Typography variant="body2">
                      <strong>Cloud Account:</strong> {displayData.cloud_account_name}
                    </Typography>
                  )}
                  {displayData.subscription_name && (
                    <Typography variant="body2">
                      <strong>Subscription:</strong> {displayData.subscription_name}
                    </Typography>
                  )}
                  {displayData.resource_group_name && (
                    <Typography variant="body2">
                      <strong>Resource Group:</strong>{' '}
                      {displayData.resource_group_name}
                    </Typography>
                  )}
                  <Typography variant="body2">
                    <strong>Resource Name:</strong>{' '}
                    {isResourceRecommendation && displayData.resource_name ? (
                      <Link
                        component="button"
                        variant="body2"
                        onClick={handleOpenResourceDetails}
                        sx={{
                          color: '#1C71E3',
                          cursor: 'pointer',
                          textDecoration: 'none',
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}>
                        {displayData.resource_name}
                      </Link>
                    ) : (
                      displayData.resource_name
                    )}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Resource Type:</strong> {displayData.resource_type}
                  </Typography>
                  {displayData.tenant_id && (
                    <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                      {displayData.resource_identifier ? (
                        <Link
                          href={`https://portal.azure.com/#@${displayData.tenant_id}/resource${displayData.resource_identifier}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: '#1C71E3',
                            textDecoration: 'none',
                            fontSize: '0.875rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                          }}>
                          Azure Portal
                          <OpenInNewIcon sx={{ fontSize: '0.875rem' }} />
                        </Link>
                      ) : displayData.subscription_id ? (
                        <Link
                          href={`https://portal.azure.com/#@${displayData.tenant_id}/resource/subscriptions/${displayData.subscription_id}/advisorRecommendations`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: '#1C71E3',
                            textDecoration: 'none',
                            fontSize: '0.875rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                          }}>
                          Azure Portal
                          <OpenInNewIcon sx={{ fontSize: '0.875rem' }} />
                        </Link>
                      ) : null}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid #E7ECF3',
                  boxShadow: '0 1px 2px rgba(16,35,58,.04)',
                }}>
                <CardContent sx={{ flex: 1 }}>
                  <Typography fontWeight="bold" color="#16233A">
                    Additional Information
                  </Typography>
                  <Typography variant="body2">
                    <strong>Status:</strong> {displayData.status}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Date Created:</strong>{' '}
                    {new Date(displayData.date_created).toLocaleDateString(
                      'en-GB',
                    )}
                  </Typography>
                  {displayData.yearly_savings && (
                    <Typography variant="body2">
                      <strong>Yearly Savings:</strong>{' '}
                      {formatCurrencyNoUnit(displayData.yearly_savings)} {displayData.currency}
                    </Typography>
                  )}
                  
                  {/* Display additional_data fields if available */}
                  {displayData.additional_data && (() => {
                    const filteredEntries = Object.entries(
                      displayData.additional_data,
                    ).filter(
                      ([, value]) =>
                        value !== null &&
                        value !== undefined &&
                        value !== 'Unknown',
                    );

                    if (filteredEntries.length === 0) {
                      return null;
                    }

                    return (
                      <>
                        <Divider sx={{ my: 1.5 }} />
                        {filteredEntries.map(([key, value]) => (
                          <Typography
                            key={key}
                            variant="body2"
                            sx={{ mb: 0.5 }}
                          >
                            <strong>
                              {key
                                .replace(/([A-Z])/g, ' $1')
                                .replace(
                                  /^./,
                                  (str) => str.toUpperCase(),
                                )}
                              :
                            </strong>{' '}
                            {typeof value === 'object'
                              ? JSON.stringify(value)
                              : String(value)}
                          </Typography>
                        ))}
                      </>
                    );
                  })()}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {displayData.tags && (
            <Box mt={2}>
              <Card
                elevation={0}
                sx={{
                  border: '1px solid #E7ECF3',
                  boxShadow: '0 1px 2px rgba(16,35,58,.04)',
                }}>
                <CardContent>
                  <Typography fontWeight="bold" color="#16233A">
                    Tags
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                    {/* @ts-ignore */}
                    {displayData.tags?.split(',').map((tag, index) => (
                      <Chip key={index} label={tag.trim()} size="small" />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}

          <Box mt={4}>
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              color="#16233A">
              Next Steps and Implementation
            </Typography>
            <ScrollableContent>
              <Typography variant="body2" paragraph>
                {displayData.action || 'To implement this recommendation and start saving costs, follow these steps: 1. Review the resource details and current usage patterns 2. Plan the implementation during a maintenance window if needed 3. Make the necessary changes to optimize the resource 4. Monitor the changes to ensure they achieve the expected savings. If you need assistance with implementation, please contact your cloud optimization team.'}
              </Typography>
            </ScrollableContent>
          </Box>
        </StyledDialogContent>

        <StyledDialogActions>
          <CancelButton onClick={onClose} disabled={loading}>
            Close
          </CancelButton>

          <StyledButton onClick={handleOpenDisputeDialog} disabled={loading}>
            Open Dispute
          </StyledButton>

          <RejectButton onClick={onMarkAsDeclined} disabled={loading}>
            Mark as rejected
          </RejectButton>

          <StyledButton onClick={onMarkAsImplemented} disabled={loading}>
            Mark as Implemented
          </StyledButton>
        </StyledDialogActions>
      </ModalPaper>

      <DisputeDialog
        open={disputeDialogOpen}
        onClose={() => {
          setDisputeDialogOpen(false);
          setActionCenterItem(null);
          setResponsibleUserId(null);
        }}
        onSubmit={handleDisputeSubmit}
        defaultReceiverId={
          responsibleUserId && user?.id !== responsibleUserId
            ? responsibleUserId
            : null
        }
        currentUserId={user?.id || 0}
        showUserSelector={
          responsibleUserId !== null && user?.id === responsibleUserId
        }
        availableUsers={availableUsers}
        zIndex={1400}
      />

    </StyledDialog>

    {disputeModalOpen && dispute[0] && (
      <DisputesModal
        Dispute={{
          ...(dispute[0] as any),
          ...(actionCenterItem ? {
            // Spread action center item (same pattern as action center-v2.tsx)
            // This ensures resource.id is the action center item ID for the View link
            id: actionCenterItem.id,
            id_recommendation: actionCenterItem.id_recommendation,
            responsible_id: actionCenterItem.responsible_id,
            action_center_id: actionCenterItem.id,
          } : {}),
          // Set id_dispute to the actual dispute ID for dispute operations
          id_dispute: (dispute[0] as any).id,
        }}
        IsModalOpened={disputeModalOpen}
        onCloseModal={() => {
          setDisputeModalOpen(false);
          setDispute([]);
          setActionCenterItem(null);
        }}
        zIndex={1400}
      />
    )}

    <ResourceDetailsModal
      open={resourceModalOpen}
      onClose={() => {
        setResourceModalOpen(false);
        setSelectedResourceId(null);
      }}
      resourceId={selectedResourceId}
      currency={currency}
    />
    </>
  );
};

export default RecommendationModal;
